/**
 * Gifts to an organization that has not claimed its listing, held for it (GH-798, DEC-046).
 *
 * ## The shape counsel's answers produce
 *
 * A donor gives to an unclaimed organization. Legally the gift is a contribution to the platform's
 * fund (DEC-002, DEC-024 — true of every gift here) carrying the organization it was **targeted**
 * at. The fund holds it in a per-organization ledger account, `unclaimed_gift_held`, until the
 * **earlier** of two things:
 *
 *  1. **The organization claims its listing.** The held balance moves into its ordinary lane —
 *     `contributions_received`, less the platform fee the gift's quote recorded — by reversing each
 *     held receipt and posting the ordinary receipt in its place. Nothing posted is ever updated.
 *  2. **Day {@link DEFAULT_HOLD_DAYS} after the gift.** The held balance becomes exactly one check
 *     disbursement, addressed to the organization's mailing address on record with the IRS, and the
 *     ledger records it the same way: reversal of the held receipt, replacement into
 *     `unclaimed_gift_checks_payable`. Operations cut and mail the check; mailing moves the payable
 *     out of `platform_cash`.
 *
 * ## Why the hold is configurable but bounded below 45
 *
 * Counsel's answer 1 meets Cal. Gov. Code §12599.9 / 11 CCR §320(a)(2) — a non-consenting
 * recipient's funds within 45 days — by *paying*, and answer 3 promises a check "within 40 days".
 * The number is configuration so operations can tighten it; it can never reach 45, because at 45 the
 * hold itself is the "misuse" §314(f) describes. {@link assertHoldDays} is the one gate, and the
 * config loader refuses the same bound at startup.
 *
 * ## Counsel calls the balance stored value (answer 5)
 *
 * That is why the whole capability sits behind `FEATURE_UNCLAIMED_ORG_GIFTS`, which production
 * refuses until DEC-011's money-transmission answer covers this balance. Nothing in this module
 * decides that; it only makes the books say exactly what happened.
 */
import { DomainError } from "./errors.js";
import { assertBalanced, donationReceivedTransaction, } from "./ledger.js";
import { isZero } from "./money.js";
import { DAY_MS } from "./clock.js";
/**
 * Who a held gift's receipt names when nothing is configured (DEC-046 answer 4: "GiveHere/BedFound").
 * `UNCLAIMED_GIFT_RECIPIENT_NAME` in `packages/config` defaults to the same words, and a test pins
 * the two together.
 */
export const DEFAULT_UNCLAIMED_GIFT_RECIPIENT_NAME = "GiveHere / Bedier Foundation";
/** Counsel's number (DEC-046 answer 3): a check within 40 days if the organization never claims. */
export const DEFAULT_HOLD_DAYS = 40;
/**
 * The first hold length that is refused. 11 CCR §320(a)(2) gives a non-consenting recipient's funds
 * 45 days; a hold of 45 or more would make holding itself the violation.
 */
export const HOLD_DAYS_CEILING_EXCLUSIVE = 45;
/** Refuse a hold that is not a whole number of days in `[1, 45)`. */
export function assertHoldDays(days) {
    if (!Number.isInteger(days) || days < 1 || days >= HOLD_DAYS_CEILING_EXCLUSIVE) {
        throw new DomainError("invalid_request", `The hold on a gift to an unclaimed organization must be a whole number of days from 1 to ${HOLD_DAYS_CEILING_EXCLUSIVE - 1}; ${String(days)} is not.`);
    }
    return days;
}
/** When a gift received at `receivedAt` must be released, if its organization has not claimed. */
export function releaseDueAt(receivedAt, holdDays) {
    return Object.freeze({ epochMs: receivedAt.epochMs + assertHoldDays(holdDays) * DAY_MS });
}
/** Whether a gift received at `receivedAt` is due for its check at `now`. */
export function isReleaseDue(receivedAt, holdDays, now) {
    return now.epochMs >= releaseDueAt(receivedAt, holdDays).epochMs;
}
/**
 * The terms sentence (DEC-046 answer 2), as the owner relayed counsel's direction, with the hold
 * length filled in from configuration so the sentence and the job can never disagree.
 */
export function heldGiftTermsSentence(holdDays = DEFAULT_HOLD_DAYS) {
    return ("GiveHere holds the funds for the organization until it claims its listing; if it has not " +
        `claimed within ${assertHoldDays(holdDays)} days, GiveHere sends the funds by check to its ` +
        "address on record with the IRS.");
}
/**
 * What the checkout says before money moves. Names the recipient the receipt will name, and never
 * says the organization has the money — it does not, and DEC-046 answer 4 makes the fund the
 * recipient.
 */
export function heldGiftNotice(input) {
    return (`${input.organizationName} has not claimed its listing on GiveHere. Your gift is made to ` +
        `${input.recipientName} and targeted for ${input.organizationName}.`);
}
/** The receipt notation (DEC-046 answer 4): the recipient, with the organization it was targeted for. */
export function heldGiftReceiptNotation(input) {
    return `Received by ${input.recipientName}. Targeted for ${input.organizationName}, which had not claimed its listing when the gift was made.`;
}
/**
 * Phrases no held-gift copy may contain. Each one tells a donor the organization already has the
 * money, which is false until it claims or the check is mailed — and on the receipt it would name
 * the wrong recipient (DEC-046 answer 4).
 */
export const HELD_GIFT_FORBIDDEN_PHRASES = [
    "has received",
    "have received",
    "received your gift",
    "received by the organization",
    "is now with",
    "was sent to",
    "has been sent",
    "your donation to the organization",
    "paid to the organization",
];
/** The cost-support legs, identical on every receipt shape: the donor's own, revenue on arrival. */
function costSupportEntries(costSupport, memo) {
    if (isZero(costSupport))
        return [];
    return [
        {
            account: "processor_balance",
            direction: "debit",
            amount: costSupport,
            memo: `${memo} (cost support)`,
        },
        {
            account: "cost_support_received",
            direction: "credit",
            amount: costSupport,
            memo: `${memo} (cost support)`,
        },
    ];
}
/**
 * A held gift arriving.
 *
 * ```
 * Dr processor_balance          donation (+ cost support)
 *    Cr unclaimed_gift_held          donation
 *    Cr cost_support_received        cost support
 * ```
 *
 * The whole gift is held — **no platform fee is netted on arrival.** The fee the quote recorded is
 * netted only if the organization claims and the gift enters its ordinary lane. A check to an
 * organization that never agreed to anything carries the whole gift (see `heldGiftCheckReplacement`).
 * The held checkout never offers cost support, so that leg is zero in practice.
 */
export function heldGiftReceivedTransaction(input) {
    const draft = {
        transactionId: input.transactionId,
        organizationId: input.organizationId,
        currency: input.donation.currency,
        memo: input.memo,
        entries: [
            {
                account: "processor_balance",
                direction: "debit",
                amount: input.donation,
                memo: input.memo,
            },
            {
                account: "unclaimed_gift_held",
                direction: "credit",
                amount: input.donation,
                memo: `${input.memo} (held for the organization)`,
            },
            ...costSupportEntries(input.costSupport, input.memo),
        ],
    };
    assertBalanced(draft);
    return draft;
}
/**
 * The replacement posted when the organization claims: the ordinary receipt the gift would have had
 * if the organization had been on the platform when it was made, with the fee its quote recorded.
 * Posted after the reversal of the held receipt, never instead of it.
 */
export function heldGiftClaimReplacement(input) {
    return donationReceivedTransaction({
        transactionId: input.transactionId,
        organizationId: input.organizationId,
        donation: input.donation,
        costSupport: input.costSupport,
        platformFee: input.platformFee,
        memo: input.memo,
    });
}
/**
 * The replacement posted on day 40: the whole gift becomes a check the platform owes the
 * organization's IRS address. No fee is netted from a check to an organization that never agreed
 * to the platform's terms — the conservative reading, recorded in `docs/evidence/GH-798.md`.
 */
export function heldGiftCheckReplacement(input) {
    const draft = {
        transactionId: input.transactionId,
        organizationId: input.organizationId,
        currency: input.donation.currency,
        memo: input.memo,
        entries: [
            {
                account: "processor_balance",
                direction: "debit",
                amount: input.donation,
                memo: input.memo,
            },
            {
                account: "unclaimed_gift_checks_payable",
                direction: "credit",
                amount: input.donation,
                memo: `${input.memo} (check to the IRS address of record)`,
            },
            ...costSupportEntries(input.costSupport, input.memo),
        ],
    };
    assertBalanced(draft);
    return draft;
}
/**
 * The check left the building.
 *
 * ```
 * Dr unclaimed_gift_checks_payable   amount
 *    Cr platform_cash                    amount
 * ```
 *
 * Recorded at mailing rather than at clearing: once mailed the platform no longer controls the
 * instrument. A check returned or never cashed is follow-up work named in the evidence note.
 */
export function heldGiftCheckMailedTransaction(input) {
    if (input.amount.amountMinor <= 0n) {
        throw new DomainError("invalid_request", "A check must be for a positive amount.");
    }
    const draft = {
        transactionId: input.transactionId,
        organizationId: input.organizationId,
        currency: input.amount.currency,
        memo: input.memo,
        entries: [
            {
                account: "unclaimed_gift_checks_payable",
                direction: "debit",
                amount: input.amount,
                memo: input.memo,
            },
            { account: "platform_cash", direction: "credit", amount: input.amount, memo: input.memo },
        ],
    };
    assertBalanced(draft);
    return draft;
}
//# sourceMappingURL=unclaimed-gifts.js.map